import { FURNITURE_CONFIG } from '~/components/ThreeDModel/furnitureConfig'
import {
  ColumnConfigurationType,
  getConfigurationMetadata,
} from '~/types/columnConfigurationTypes'
import type { ColumnConfigurationWithOptions } from '~/types/furniture3D'
import { computeSidePanelDimensions } from '~/utils/computeSidePanelDimensions'
import { computeTopPanelDimensions } from '~/utils/computeTopPanelDimensions'
import type { PanelPart } from '~/context/wardrobePartsContext'

const { panelThickness, panelSpacing, drawerInsidePanelsOffset } = FURNITURE_CONFIG

export interface StandPartsArgs {
  width: number
  height: number
  depth: number
  plintHeight: number
  columns: number
  /** Legacy fallback section count (mirrors Column.tsx sectionsCount). */
  sections: number
  columnWidths?: number[]
  columnConfigurationsWithOptions?: ColumnConfigurationWithOptions[]
  columnConfigurations?: ColumnConfigurationType[]
}

export interface StandParts {
  panels: PanelPart[]
  /** Number of door facades (for the pricing `doorsNumber` option). */
  doorsNumber: number
  /** One zone record per column (type + shelf/drawer counts) for the backend. */
  zones: Record<string, number | boolean | string>[]
}

/** The five PAL panels of one drawer box (front + back + bottom + two sides). */
function drawerPanels(
  drawerWidth: number,
  drawerHeight: number,
  drawerDepth: number
): PanelPart[] {
  const innerHeight = drawerHeight - panelSpacing
  const innerWidth = drawerWidth - panelThickness * 2
  const innerDepth = drawerDepth - panelThickness * 2
  const sideHeight = innerHeight - drawerInsidePanelsOffset

  return [
    { width: drawerWidth, height: innerHeight, quantity: 1 }, // front
    { width: innerWidth, height: innerDepth, quantity: 1 }, // bottom
    { width: innerDepth, height: sideHeight, quantity: 2 }, // left + right
    { width: innerWidth, height: sideHeight, quantity: 1 }, // back
  ]
}

/**
 * Enumerate every PAL panel the stand 3D scene draws — carcass (sides, top,
 * plinth), per-column back + bottom, dividers, shelves, drawer boxes and door
 * facades — so the pricing quote matches the model.
 *
 * Mirrors: products/stand/builder.tsx (carcass + dividers) and parts/Column.tsx
 * (per-column panels, shelves, drawers, doors). Runners/handles/hinges are metal
 * and excluded.
 */
export function computeStandParts(args: StandPartsArgs): StandParts {
  const { width, height: H, depth: D, plintHeight, columns, sections } = args

  const widths =
    args.columnWidths && args.columnWidths.length === columns
      ? args.columnWidths
      : Array.from({ length: columns }, () => width / columns)

  const panels: PanelPart[] = []
  const zones: Record<string, number | boolean | string>[] = []
  let doorsNumber = 0

  // Carcass: two side panels + top + plinth (SidePanels + TopAndPlinth).
  const sides = computeSidePanelDimensions({
    width,
    height: H,
    depth: D,
    isNewCollection: false,
    isSingleNarrowColumn: false,
    isTwoSingleDoorsException: false,
    isMirrored: false,
  })
  const top = computeTopPanelDimensions({ width, depth: D, isNewCollection: false })
  panels.push(
    { ...sides.left, quantity: 1 },
    { ...sides.right, quantity: 1 },
    { width: top.width, height: top.height, quantity: 1 },
    { width, height: plintHeight, quantity: 1 } // plinth
  )

  // Dividers between adjacent columns (ColumnDivider).
  for (let i = 0; i < columns - 1; i++) {
    panels.push({
      width: D - 2 * panelThickness,
      height: H - plintHeight - panelThickness,
      quantity: 1,
    })
  }

  for (let i = 0; i < columns; i++) {
    const W = widths[i]
    if (!W) continue

    const columnType =
      args.columnConfigurationsWithOptions?.[i]?.type ||
      args.columnConfigurations?.[i] ||
      ColumnConfigurationType.DRAWERS_3
    const metadata = getConfigurationMetadata(columnType)

    // One zone per stand column: carries its config + shelf/drawer counts.
    zones.push({
      type: columnType,
      height: H,
      shelves: metadata?.shelfCount ?? 0,
      drawers: metadata?.drawerCount ?? 0,
      hasShelfAtTop: false,
    })

    // Per-column carcass: back + bottom (Column.tsx panels memo).
    panels.push(
      { width: W, height: H, quantity: 1 }, // back
      { width: W, height: D, quantity: 1 } // bottom
    )

    // Section sizing (Column.tsx columnConfig memo).
    const actualSections =
      metadata?.hasDrawers && metadata?.hasDoors
        ? metadata.shelfCount + 1
        : metadata?.drawerCount > 0
          ? metadata.drawerCount
          : (metadata?.shelfCount ?? 0) + 1
    const usableSectionsCount = actualSections > 0 ? actualSections : sections

    const usableHeight = H - panelThickness - plintHeight
    const singleSectionTotalHeight = usableHeight / usableSectionsCount
    const sectionHeight = singleSectionTotalHeight - panelSpacing
    const sectionWidth = W - panelSpacing

    // Internal shelves (renderShelves): one PAL panel each.
    const shelfCount = metadata?.shelfCount ?? 0
    if (shelfCount > 0) {
      const doorWidth =
        metadata?.doorCount === 2 ? W : W - panelSpacing
      for (let s = 0; s < shelfCount; s++) {
        panels.push({
          width: doorWidth - panelThickness,
          height: D - panelThickness,
          quantity: 1,
        })
      }
    }

    // Mixed: 1 drawer + door(s).
    if (metadata?.hasDrawers && metadata?.hasDoors) {
      panels.push(...drawerPanels(sectionWidth, sectionHeight, D))
      const doorHeight = singleSectionTotalHeight * (metadata.shelfCount ?? 0)
      if (metadata.doorCount === 2) {
        const doorWidth = W / 2 - panelSpacing / 2
        panels.push({ width: doorWidth, height: doorHeight, quantity: 2 })
        doorsNumber += 2
      } else {
        panels.push({ width: sectionWidth, height: doorHeight, quantity: 1 })
        doorsNumber += 1
      }
      continue
    }

    // Drawers only.
    if (metadata?.hasDrawers) {
      for (let d = 0; d < usableSectionsCount; d++) {
        panels.push(...drawerPanels(sectionWidth, sectionHeight, D))
      }
      continue
    }

    // Doors only (single or split) + shelves already pushed.
    if (metadata?.hasDoors) {
      const doorHeight = H - plintHeight - panelThickness
      if (metadata.doorCount === 2) {
        const doorWidth = W / 2 - panelSpacing / 2
        panels.push({ width: doorWidth, height: doorHeight, quantity: 2 })
        doorsNumber += 2
      } else {
        panels.push({ width: sectionWidth, height: doorHeight, quantity: 1 })
        doorsNumber += 1
      }
    }
  }

  return { panels, doorsNumber, zones }
}
